import QrCodeIcon from '@mui/icons-material/QrCode';
import { Box, Button, Card, Stack, TextField, Typography } from '@mui/material';
import { useAddTagLink } from 'gen/client';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { useHasNext } from '@ui/Hooks/useHasNext';
import getCDNImagePath from '@ui/Utils/getCDNImagePath';
import { customPromiseToast, redirectTagPromise } from '@ui/Utils/toasts';

import renkiTagLogo from '../../../../public/renkiTag.png';
import ViewContainer from '../../../Components/Layout/ViewContainer';

// Define the tag type interface
interface TagType {
    id: string;
    isQR: boolean;
    label: string;
    serial: string;
}

// Define tag types with their corresponding serial numbers and whether they use QR or NFC
const tagTypes: TagType[] = [
    { id: 'qr', isQR: true, label: 'QR Tag', serial: '1' },
    { id: 'plastic', isQR: false, label: 'Plastic Tag', serial: '459267a405980' },
    { id: 'paper', isQR: false, label: 'Paper Tag', serial: '4bf2694c12a81' },
    { id: 'card', isQR: false, label: 'Renki Card', serial: '411a7c92b0289' },
    { id: 'tablestand', isQR: false, label: 'Table Stand', serial: '4c2107a8b1194' },
    { id: 'keyfob', isQR: false, label: 'Key Fob', serial: '48b521aea6f80' },
];

// Example URLs
const exampleUrls = [
    'https://renki.app/',
    'https://www.missing-link.fi/',
    'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
];

interface TagFormInputs {
    customUrl: string;
    selectedUrl: string;
    tagType: string;
}

export default function TestRenkiTag() {
    const hasNext = useHasNext();
    const { trigger: addTagRedirect } = useAddTagLink();

    const [selectedTag, setSelectedTag] = useState<TagType>(tagTypes[0]!);
    const [selectedUrl, setSelectedUrl] = useState('');

    const { control, handleSubmit, setValue, watch } = useForm<TagFormInputs>({
        defaultValues: {
            customUrl: '',
            selectedUrl: '',
            tagType: 'qr',
        },
    });

    if (!hasNext) {
        return null;
    }

    const customUrl = watch('customUrl');

    // if (!hasNext) {
    //     return;
    // }

    const onSubmit: SubmitHandler<TagFormInputs> = async (data) => {
        // Use custom URL if provided, otherwise use the selected URL
        let url = data.customUrl || selectedUrl;

        // Add https:// prefix if not already present
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            url = `https://${url}`;
        }

        try {
            await customPromiseToast(addTagRedirect({ link: url, serial: selectedTag.serial }), redirectTagPromise);
        } catch (error) {
            console.error('Error adding tag redirect:', error);
        }
    };

    // Get image based on selected tag type
    const getTagImage = (tagId: string) => {
        switch (tagId) {
            case 'qr':
                return <QrCodeIcon sx={{ height: 'auto', width: 60 }} />;
            case 'card':
                return <Card elevation={4} sx={{ border: '1px solid gray', height: 50, width: 100 }} />;
            case 'plastic':
                return (
                    <Card elevation={4} sx={{ border: '1px solid gray', borderRadius: '50%', height: 50, width: 50 }} />
                );
            case 'paper':
                return (
                    <Card
                        elevation={4}
                        sx={{
                            bgcolor: '#d7ccc8',
                            border: '1px solid gray',
                            borderRadius: '50%',
                            height: 50,
                            width: 50,
                        }}
                    />
                );
            case 'keyfob':
                return (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Main keyfob body */}
                        <Card
                            elevation={4}
                            sx={{
                                alignItems: 'center',
                                bgcolor: '#424242',
                                border: '1px solid #2c2c2c',
                                borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                                display: 'flex',
                                height: 55,
                                justifyContent: 'center',
                                mb: 0.5,
                                position: 'relative',
                                width: 40,
                            }}
                        >
                            {/* Inner circle (chip area) */}
                            <Box
                                sx={{
                                    bgcolor: 'transparent',
                                    border: '2px solid #2c2c2c',
                                    borderRadius: '50%',
                                    height: 32,
                                    position: 'absolute',
                                    top: 16,
                                    width: 32,
                                }}
                            />
                            {/* Ring hole */}
                            <Box
                                sx={{
                                    bgcolor: 'white',
                                    border: '1px solid #2c2c2c',
                                    borderRadius: '8px',
                                    height: 10,
                                    position: 'absolute',
                                    top: 4,
                                    width: 16,
                                }}
                            />
                        </Card>
                    </Box>
                );
            case 'tablestand':
                return (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            height: 50,
                            justifyContent: 'center',
                            position: 'relative',
                            width: 60,
                        }}
                    >
                        {/* Vertical part of the table stand */}
                        <Card
                            elevation={4}
                            sx={{
                                alignItems: 'flex-start',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid gray',
                                display: 'flex',
                                height: '100%',
                                justifyContent: 'center',
                                pt: 2,
                                width: '70%',
                            }}
                        >
                            <img
                                alt="Table Stand"
                                src={getCDNImagePath('renki/renkilogo_text.png')}
                                style={{ height: 'auto', maxWidth: '70%' }}
                            />
                        </Card>
                        {/* Base of the table stand */}
                        <Card
                            elevation={4}
                            sx={{
                                backgroundColor: '#e0e0e0',
                                border: '1px solid gray',
                                bottom: 0,
                                height: '10%',
                                position: 'absolute',
                                width: '100%',
                            }}
                        />
                    </Box>
                );
            default:
                return null;
        }
    };

    // Handle selecting a tag type
    const handleTagSelect = (tag: TagType) => {
        setSelectedTag(tag);
        setValue('tagType', tag.id);
    };

    // Handle selecting an example URL
    const handleUrlSelect = (url: string) => {
        setSelectedUrl(url);
        setValue('selectedUrl', url);
    };

    // Handle resetting all tags to the default URL
    const handleResetAllTags = async () => {
        try {
            // Reset each tag with the default URL
            for (const tag of tagTypes) {
                if (tag.serial === '0') {
                    return;
                }
                await customPromiseToast(
                    addTagRedirect({ link: 'https://renki.app', serial: tag.serial }),
                    redirectTagPromise,
                );
            }
        } catch (error) {
            console.error('Error resetting tags:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flex: 1, width: '100%' }}>
            <ViewContainer>
                <Stack sx={{ flex: 1, py: { md: 0, xs: 2 }, width: '100%' }}>
                    <Stack
                        spacing={{ lg: 3, xs: 2 }}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flex: 1,
                            justifyContent: 'center',
                            maxWidth: 900,
                            mx: 'auto',
                            px: 2,
                        }}
                    >
                        <Box sx={{ maxWidth: '40%', mb: 2, width: 'auto' }}>
                            <img alt="Renki Tag Logo" src={renkiTagLogo} style={{ height: 'auto', maxWidth: '100%' }} />
                        </Box>

                        <Box
                            sx={{
                                alignItems: 'flex-start',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <Typography
                                sx={{ display: { fontSize: 20, fontWeight: 'bold', lg: 'initial', xs: 'none' } }}
                            >
                                Test
                            </Typography>
                            <Typography
                                sx={{ display: { fontSize: 20, fontWeight: 'bold', lg: 'none', xs: 'initial' } }}
                            >
                                Test
                            </Typography>

                            <Typography
                                sx={{
                                    textAlign: 'left',
                                    width: '100%',
                                }}
                                variant="subtitle1"
                            >
                                Select Tag Type
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                justifyContent: 'flex-start', // Changed from center to flex-start for better alignment
                                width: '100%',
                            }}
                        >
                            {tagTypes.map((tag) => (
                                <Box
                                    key={tag.id}
                                    onClick={() => handleTagSelect(tag)}
                                    sx={{
                                        position: 'relative',
                                        // Modified to ensure 3 items per row with proper spacing
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                            borderColor: '#1C4E71',
                                        },
                                        alignItems: 'center',
                                        backgroundColor: selectedTag.id === tag.id ? '#f0f7ff' : 'transparent',
                                        border: '1px solid #e0e0e0',
                                        borderColor: selectedTag.id === tag.id ? '#1C4E71' : '#e0e0e0',
                                        borderRadius: 2,
                                        boxShadow:
                                            selectedTag.id === tag.id
                                                ? '0 0 5px rgba(0,0,0,0.75)'
                                                : '0 0 5px rgba(0,0,0,0.5)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: { sm: 140, xs: 140 },
                                        justifyContent: 'center',
                                        padding: 2,
                                        transition: 'all 0.2s',
                                        width: { sm: 'calc(33.33% - 10.67px)', xs: 'calc(50% - 8px)' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: selectedTag.id === tag.id ? '#1C4E71' : 'transparent',
                                            border: '1px solid #1C4E71',
                                            borderRadius: '50%',
                                            height: 16,
                                            position: 'absolute',
                                            right: 8,
                                            top: 8,
                                            width: 16,
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            height: 80,
                                            justifyContent: 'center',
                                            width: 80,
                                        }}
                                    >
                                        {getTagImage(tag.id)}
                                    </Box>
                                    <Typography
                                        sx={{
                                            mt: 1,
                                            textAlign: 'center',
                                        }}
                                        variant="body2"
                                    >
                                        {tag.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* URL selection section */}
                        <Box sx={{ width: '100%' }}>
                            <Typography
                                sx={{
                                    mb: 1,
                                    textAlign: 'left',
                                }}
                                variant="subtitle1"
                            >
                                Enter custom URL
                            </Typography>
                            <Controller
                                control={control}
                                name="customUrl"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Custom URL"
                                        placeholder="example.com (https:// will be added automatically)"
                                        size="small"
                                        sx={{ mb: 1 }}
                                        variant="outlined"
                                    />
                                )}
                            />

                            <Typography
                                sx={{
                                    mb: 1,
                                    textAlign: 'left',
                                }}
                                variant="subtitle1"
                            >
                                Or select URL
                            </Typography>
                            <Stack spacing={2} sx={{ width: '100%' }}>
                                {exampleUrls.map((url) => (
                                    <Box
                                        key={url}
                                        onClick={() => !customUrl && handleUrlSelect(url)}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: customUrl ? 'transparent' : '#f5f5f5',
                                                borderColor: customUrl ? '#e0e0e0' : '#1C4E71',
                                            },
                                            alignItems: 'center',
                                            backgroundColor:
                                                selectedUrl === url && !customUrl ? '#f0f7ff' : 'transparent',
                                            border: '1px solid #e0e0e0',
                                            borderColor: selectedUrl === url && !customUrl ? '#1C4E71' : '#e0e0e0',
                                            borderRadius: 1,
                                            cursor: customUrl ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            opacity: customUrl ? 0.5 : 1,
                                            padding: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor:
                                                    selectedUrl === url && !customUrl ? '#1C4E71' : 'transparent',
                                                border: '1px solid #1C4E71',
                                                borderRadius: '50%',
                                                height: 20,
                                                mr: 2,
                                                width: 20,
                                            }}
                                        />
                                        <Typography variant="body2">{url}</Typography>
                                    </Box>
                                ))}
                            </Stack>

                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 4, width: '100%' }}
                            >
                                <Button onClick={handleResetAllTags} variant="outlined">
                                    Reset Tags
                                </Button>
                                <Button disabled={!customUrl && !selectedUrl} type="submit" variant="contained">
                                    Set
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                </Stack>
            </ViewContainer>
        </form>
    );
}
